import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import {
  Kanban, BarChart3, Settings, Upload, LogOut, Zap, Users2,
  ShieldCheck, UserCircle2, Crown, LayoutDashboard, CheckSquare, PlugZap,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useWS } from "../../context/WebSocketContext";
import api from "../../lib/api";
import { isSuperAdmin as isPlatformSuperAdmin, normalizeRole } from "../../lib/features";

const NAV_ALL = [
  { to: "/", label: "Funil", icon: Kanban, testid: "nav-kanban" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/tasks", label: "Tarefas", icon: CheckSquare, testid: "nav-tasks", hasBadge: true },
  { to: "/automations", label: "Automações", icon: Zap, testid: "nav-automations" },
  { to: "/integrations", label: "Integrações", icon: PlugZap, testid: "nav-integrations" },
  { to: "/migration", label: "Importar", icon: Upload, testid: "nav-migration" },
  { to: "/settings", label: "Configurações", icon: Settings, testid: "nav-settings" },
];

const NAV_ADMIN = [
  { to: "/team", label: "Equipe", icon: Users2, testid: "nav-team" },
];

const NAV_SUPER_ADMIN = [
  { to: "/admin", label: "Painel SaaS", icon: LayoutDashboard, testid: "nav-admin" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { on } = useWS();
  const [expanded, setExpanded] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const normalizedRole = normalizeRole(user?.role);
  const isSuperAdmin = isPlatformSuperAdmin(user);
  const isAdmin = ["owner", "admin", "super_admin"].includes(normalizedRole);

  // Fetch pending task count for badge
  const fetchPending = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks/dashboard");
      const mine = (data?.mine || 0);
      const pending = (data?.pending || 0) + (data?.in_progress || 0);
      // Show tasks that need attention: pending + in_progress (not completed)
      setPendingCount(Math.min(mine, 99));
    } catch {}
  }, []);

  useEffect(() => {
    if (user) fetchPending();
  }, [user, fetchPending]);

  // Update badge via realtime events
  useEffect(() => {
    const u1 = on("task.created", () => fetchPending());
    const u2 = on("task.updated", () => fetchPending());
    const u3 = on("task.deleted", () => fetchPending());
    return () => { u1?.(); u2?.(); u3?.(); };
  }, [on, fetchPending]);

  return (
    <aside
      data-testid="app-sidebar"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      className="shrink-0 h-screen bg-slate-950 border-r border-slate-800 flex flex-col overflow-hidden transition-all duration-300 ease-in-out z-20"
      style={{ width: expanded ? "16rem" : "4rem" }}
    >
      {/* Logo */}
      <div className="h-16 px-3 flex items-center gap-3 border-b border-slate-800 overflow-hidden">
        <div className="w-9 h-9 rounded-lg bg-whatsapp flex items-center justify-center shrink-0">
          <Zap className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
        </div>
        <div
          className="overflow-hidden transition-all duration-200"
          style={{ opacity: expanded ? 1 : 0, width: expanded ? "auto" : 0 }}
        >
          <div className="text-base font-bold tracking-tight text-slate-50 whitespace-nowrap" style={{ fontFamily: "Outfit" }}>
            Cliver
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 -mt-0.5 whitespace-nowrap">
            CRM Omnichannel
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-2 flex flex-col gap-0.5 overflow-y-auto overflow-x-hidden">
        {NAV_ALL.map((item) => (
          <SidebarNavItem
            key={item.to}
            item={item}
            expanded={expanded}
            badge={item.hasBadge && pendingCount > 0 ? pendingCount : 0}
          />
        ))}

        {isAdmin && (
          <>
            <div className="my-2 px-2">
              <div className="h-px bg-slate-800" />
              <div
                className="overflow-hidden transition-all duration-200 mt-2"
                style={{ opacity: expanded ? 1 : 0, maxHeight: expanded ? "2rem" : 0 }}
              >
                <p className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold px-1 whitespace-nowrap">
                  Administração
                </p>
              </div>
            </div>
            {NAV_ADMIN.map((item) => (
              <SidebarNavItem key={item.to} item={item} expanded={expanded} />
            ))}
          </>
        )}

        {isSuperAdmin && (
          <>
            <div className="my-2 px-2">
              <div className="h-px bg-amber-500/20" />
              <div
                className="overflow-hidden transition-all duration-200 mt-2"
                style={{ opacity: expanded ? 1 : 0, maxHeight: expanded ? "2rem" : 0 }}
              >
                <p className="text-[10px] uppercase tracking-widest text-amber-500/70 font-semibold px-1 whitespace-nowrap">
                  Super Admin
                </p>
              </div>
            </div>
            {NAV_SUPER_ADMIN.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                title={!expanded ? item.label : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium transition-all overflow-hidden ${
                    isActive
                      ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                      : "text-amber-500/70 hover:text-amber-400 hover:bg-amber-500/10"
                  }`
                }
              >
                <item.icon className="w-[18px] h-[18px] shrink-0" />
                <span
                  className="whitespace-nowrap overflow-hidden transition-all duration-200"
                  style={{ opacity: expanded ? 1 : 0, width: expanded ? "auto" : 0 }}
                >
                  {item.label}
                </span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* User section */}
      <div className="p-2 border-t border-slate-800 overflow-hidden">
        <div className="flex items-center gap-3 px-1 py-2">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${
              isSuperAdmin
                ? "bg-gradient-to-br from-amber-400 to-orange-500 text-slate-950"
                : "bg-gradient-to-br from-[#25D366] to-[#1EAC52] text-slate-950"
            }`}
          >
            {(user?.name || "A").slice(0, 1).toUpperCase()}
          </div>

          <div
            className="flex-1 min-w-0 overflow-hidden transition-all duration-200"
            style={{ opacity: expanded ? 1 : 0, width: expanded ? "auto" : 0 }}
          >
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-slate-100 truncate whitespace-nowrap">{user?.name}</span>
              {isSuperAdmin ? (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-400 shrink-0 whitespace-nowrap">
                  <Crown className="w-2.5 h-2.5" />SaaS
                </span>
              ) : isAdmin ? (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-[#25D366]/15 text-[#25D366] shrink-0 whitespace-nowrap">
                  <ShieldCheck className="w-2.5 h-2.5" />Admin
                </span>
              ) : (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-blue-500/15 text-blue-400 shrink-0 whitespace-nowrap">
                  <UserCircle2 className="w-2.5 h-2.5" />Agente
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 truncate whitespace-nowrap">{user?.email}</div>
          </div>

          <button
            data-testid="logout-btn"
            onClick={logout}
            style={{ opacity: expanded ? 1 : 0, pointerEvents: expanded ? "auto" : "none" }}
            className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-900 rounded-md transition shrink-0"
            title="Sair"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        {/* Collapsed logout */}
        {!expanded && (
          <button
            data-testid="logout-btn-collapsed"
            onClick={logout}
            className="w-full flex items-center justify-center p-2 text-slate-500 hover:text-red-400 hover:bg-slate-900 rounded-md transition mt-1"
            title="Sair"
          >
            <LogOut className="w-4 h-4" />
          </button>
        )}
      </div>
    </aside>
  );
}

//  Nav item with optional badge

function SidebarNavItem({ item, expanded, badge = 0 }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      data-testid={item.testid}
      title={!expanded ? item.label : undefined}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium transition-all overflow-hidden ${
          isActive
            ? "bg-slate-900 text-[#25D366]"
            : "text-slate-400 hover:text-slate-100 hover:bg-slate-900/60"
        }`
      }
    >
      <div className="relative shrink-0">
        <item.icon className="w-[18px] h-[18px]" />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] rounded-full bg-[#25D366] text-slate-950 text-[8px] font-bold flex items-center justify-center px-0.5">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <span
        className="whitespace-nowrap overflow-hidden transition-all duration-200"
        style={{ opacity: expanded ? 1 : 0, width: expanded ? "auto" : 0 }}
      >
        {item.label}
      </span>
      {badge > 0 && expanded && (
        <span className="ml-auto shrink-0 min-w-[18px] h-[18px] rounded-full bg-[#25D366]/20 text-[#25D366] text-[10px] font-bold flex items-center justify-center px-1 border border-[#25D366]/30">
          {badge}
        </span>
      )}
    </NavLink>
  );
}
